import React, { useState } from "react";
import { Modal, Form, Input, Select, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

interface AbsentScheduleModalProps {
  open: boolean;
  onClose: () => void;
  onMarkAbsent: (absentReason: string) => Promise<boolean>;
  scheduleName?: string;
}

const AbsentScheduleModal: React.FC<AbsentScheduleModalProps> = ({
  open,
  onClose,
  onMarkAbsent,
  scheduleName,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const absenceReasonOptions = [
    { label: "No-show", value: "No-show" },
    { label: "Sick leave", value: "Sick leave" },
    { label: "Family emergency", value: "Family emergency" },
    { label: "Transportation issue", value: "Transportation issue" },
    { label: "Personal emergency", value: "Personal emergency" },
    { label: "Other", value: "Other" },
  ];

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const reasonType = values.absenceType as string;
      const otherReason = (values.otherReason as string | undefined)?.trim();
      const selectedReason =
        reasonType === "Other" ? otherReason || "Other" : reasonType;

      const success = await onMarkAbsent(selectedReason);

      if (success) {
        message.success("Schedule marked as absent successfully");
        form.resetFields();
        onClose();
      } else {
        message.error("Failed to mark schedule as absent");
      }
    } catch (error) {
      console.error("Validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span>
          <ExclamationCircleOutlined
            style={{ color: "#faad14", marginRight: 8 }}
          />
          Mark Schedule as Absent
        </span>
      }
      open={open}
      onOk={handleSubmit}
      onCancel={() => {
        onClose();
        form.resetFields();
      }}
      confirmLoading={loading}
      okText="Mark as Absent"
      okButtonProps={{ danger: true }}
      width={500}
    >
      <div style={{ marginBottom: 16 }}>
        {scheduleName && (
          <p>
            You are about to mark <strong>{scheduleName}</strong> as absent.
          </p>
        )}
        <p>Please provide a reason for the absence:</p>
      </div>

      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="absenceType"
          label="Absence Type"
          rules={[{ required: true, message: "Please select an absence type" }]}
        >
          <Select
            options={absenceReasonOptions}
            placeholder="Select absence reason"
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev, current) =>
            prev.absenceType !== current.absenceType
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("absenceType") === "Other" ? (
              <Form.Item
                name="otherReason"
                label="Other Reason"
                rules={[
                  {
                    required: true,
                    message: "Please provide an absence reason",
                  },
                  {
                    max: 500,
                    message: "Reason cannot exceed 500 characters",
                  },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Enter custom absence reason"
                />
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AbsentScheduleModal;
